import { ApiService } from './apiService';
import { triggerBlobDownload } from './userInvoiceService';
import { API_ENDPOINTS } from '../config/api';
import {
  User,
  UserDetail,
  UserOtherImage,
  CreateUserRequest,
  UpdateUserRequest,
  PaginatedResponse,
  BulkExportUsersRequest,
  BulkExportUsersResponse,
  SearchUsersRequest,
  UserSearchApiRow,
  UserListItem,
  UserSearchStatus,
  mapUserSearchRow,
} from '../types/api';
import { appendQueryString } from '../utils/queryParams';

export interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  paid?: boolean;
}

const normalizeProfilePictureId = (raw: Record<string, unknown>): number | null => {
  const value = raw.profilePictureId ?? raw.profile_picture_id;
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizeOtherImages = (raw: Record<string, unknown>): UserOtherImage[] => {
  if (!Array.isArray(raw.otherImages)) {
    return [];
  }

  return raw.otherImages.filter(
    (image): image is UserOtherImage =>
      typeof image === 'object' &&
      image !== null &&
      typeof (image as UserOtherImage).url === 'string'
  );
};

const normalizeUserResponse = (raw: Record<string, unknown>): UserDetail => {
  const user = raw as unknown as UserDetail;
  const otherImages = normalizeOtherImages(raw);
  const photosFromOtherImages = otherImages.map((image) => image.url).filter(Boolean);
  const isBlocked = Boolean(raw.is_blocked ?? user.isBlocked);
  const rawStatus =
    typeof raw.status === 'string' ? (raw.status as UserSearchStatus) : user.status;

  return {
    ...user,
    createdAt:
      typeof raw.createdAt === 'string'
        ? raw.createdAt
        : typeof raw.created_at === 'string'
          ? raw.created_at
          : user.createdAt,
    otherImages,
    photos: photosFromOtherImages.length > 0 ? photosFromOtherImages : user.photos,
    profilePictureId: normalizeProfilePictureId(raw) ?? user.profilePictureId ?? null,
    isBlocked,
    status: isBlocked ? 'blocked' : rawStatus,
  };
};

const blobFromBase64 = (contentBase64: string, contentType: string): Blob => {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
};

/**
 * User Service - Admin User Management
 */
export class UserService extends ApiService {
  /**
   * Get all users with pagination and search
   * GET /admin/user/all?page=1&limit=10&search=query
   */
  async getUsers(params?: UsersQueryParams): Promise<PaginatedResponse<User>> {
    const url = appendQueryString(API_ENDPOINTS.users.list, {
      page: params?.page || 1,
      limit: params?.limit || 10,
      search: params?.search,
      paid: params?.paid ? 'true' : undefined,
    });
    return this.get<PaginatedResponse<User>>(url);
  }

  /**
   * Search users with filters
   * POST /admin/user/search
   */
  async searchUsers(body: SearchUsersRequest): Promise<PaginatedResponse<UserListItem>> {
    const response = await this.post<PaginatedResponse<UserSearchApiRow>>(
      API_ENDPOINTS.users.search,
      body
    );
    return {
      ...response,
      data: response.data.map(mapUserSearchRow),
    };
  }

  /**
   * Get user by ID
   * GET /admin/user/{id}
   */
  async getUserById(id: string): Promise<UserDetail> {
    const response = await this.get<Record<string, unknown>>(API_ENDPOINTS.users.get(id));
    return normalizeUserResponse(response);
  }

  /**
   * Block user by ID
   * POST /admin/user/block/{id}
   */
  async blockUser(id: string): Promise<User> {
    return this.post<User>(API_ENDPOINTS.users.block(id));
  }

  /**
   * Unblock user by ID
   * POST /admin/user/unblock/{id}
   */
  async unblockUser(id: string): Promise<User> {
    return this.post<User>(API_ENDPOINTS.users.unblock(id));
  }

  /**
   * Resend invitation email
   * POST /admin/user/{id}/send-invite
   */
  async sendInvite(id: string): Promise<void> {
    await this.post<void>(API_ENDPOINTS.users.sendInvite(id));
  }

  /**
   * Create new user
   * POST /admin/user
   */
  async createUser(userData: CreateUserRequest): Promise<User> {
    return this.post<User>(API_ENDPOINTS.users.create, userData);
  }

  /**
   * Update user
   * PUT /admin/user/{id}
   */
  async updateUser(id: string, userData: UpdateUserRequest): Promise<User> {
    return this.patch<User>(API_ENDPOINTS.users.update(id), userData);
  }

  /**
   * Delete user
   * DELETE /admin/user/{id}
   */
  async deleteUser(id: string): Promise<void> {
    return this.delete<void>(API_ENDPOINTS.users.delete(id));
  }

  async hideUser(id: string): Promise<void> {
    return this.post<void>(API_ENDPOINTS.users.hide(id));
  }

  async unhideUser(id: string): Promise<void> {
    return this.post<void>(API_ENDPOINTS.users.unhide(id));
  }

  /**
   * Assign plan to user
   * POST /admin/user/assign-plan
   */
  async assignPlan(data: {
    user_id: number;
    plan_detail_id: number;
    note_from_admin: string;
    currency: string;
    collection_type: 'online' | 'cash';
  }): Promise<{ message: string }> {
    return this.post<{ message: string }>(API_ENDPOINTS.users.assignPlan, data);
  }

  async bulkExport(body: BulkExportUsersRequest): Promise<BulkExportUsersResponse> {
    return this.post<BulkExportUsersResponse>(API_ENDPOINTS.users.bulkExport, body);
  }

  async exportAndDownload(body: BulkExportUsersRequest): Promise<void> {
    const format = body.format ?? 'xlsx';
    const response = await this.bulkExport({ ...body, format });

    let blob: Blob;
    if (response.contentBase64) {
      blob = blobFromBase64(response.contentBase64, response.contentType);
    } else if (response.csv) {
      blob = new Blob([response.csv], { type: response.contentType });
    } else {
      throw new Error('Export response missing file content');
    }

    triggerBlobDownload(blob, response.filename);
  }
}

// Export singleton instance
export const userService = new UserService();
